<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <header class="slips-header">
        <h1 class="dashboard-title">Lieferscheine</h1>
        <div class="slips-actions">
          <input
            v-model="filter"
            type="search"
            class="form-control slips-filter"
            placeholder="Nummer, Absender oder Empfänger…"
            aria-label="Lieferscheine filtern"
          />
          <select
            v-model="direction"
            class="form-select slips-direction"
            aria-label="Richtung"
          >
            <option value="">Beide Richtungen</option>
            <option value="to_partner">Nur hinaus</option>
            <option value="from_partner">Nur zurück</option>
          </select>
          <button type="button" class="btn btn-primary" @click="onCreateDeliverySlip">
            Lieferschein erstellen
          </button>
        </div>
      </header>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="loadError" class="error">{{ loadError }}</div>
      <div v-else-if="items.length === 0" class="slips-empty">
        Noch kein Lieferschein erstellt. Lieferscheine hinaus entstehen aus den
        Geräten, die über „Versand an Servicepartner“ vorbereitet wurden;
        Rücklieferscheine erstellt sich der Servicepartner im Partnerportal.
      </div>
      <div v-else-if="visibleItems.length === 0" class="slips-empty">
        Kein Lieferschein passt zum Filter.
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle">
          <thead>
            <tr>
              <th scope="col" style="width: 100px;">Nummer</th>
              <th scope="col" style="width: 90px;">Richtung</th>
              <th scope="col">Absender</th>
              <th scope="col">Empfänger</th>
              <th scope="col" style="width: 150px;">Erstellt</th>
              <th scope="col">Ersteller</th>
              <th scope="col" style="width: 80px;" class="slips-count">Geräte</th>
              <th scope="col" style="width: 110px;" class="slips-count">Angekommen</th>
              <th scope="col" style="width: 190px;"><span class="visually-hidden">Aktionen</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="slip in visibleItems" :key="slip.id">
              <td data-label="Nummer" class="slips-number">{{ slip.slip_number }}</td>
              <!-- Die Nummer sagt die Richtung nicht (eine Reihe für beide),
                   deshalb steht sie hier als Abzeichen. -->
              <td data-label="Richtung">
                <span :class="isReturn(slip) ? 'dir-back' : 'dir-out'">
                  {{ isReturn(slip) ? 'zurück' : 'hinaus' }}
                </span>
              </td>
              <td data-label="Absender">{{ slip.sender || '—' }}</td>
              <td data-label="Empfänger">{{ slip.recipient || '—' }}</td>
              <td data-label="Erstellt">{{ formatDate(slip.creation_timestamp) }}</td>
              <td data-label="Ersteller">{{ slip.created_by || '—' }}</td>
              <td data-label="Geräte" class="slips-count">{{ slip.device_count }}</td>
              <!-- Teilabschluss ist beidseits erlaubt. Was fehlt, muss sichtbar
                   sein — sonst nützt die Meldung nichts. -->
              <td data-label="Angekommen" class="slips-count">
                <span :class="takenClass(slip)">{{ slip.arrived_count }} von {{ slip.device_count }}</span>
              </td>
              <td data-label="Aktionen" class="slips-row-actions">
                <!-- Details gibt es nur hinaus: die Positionen einer Rücksendung
                     liegen in einer anderen Tabelle und tragen weder Fehlerbild
                     noch Preis, die dieser Dialog zeigt. Das Papier sagt dort
                     alles, was auf ihm steht. -->
                <button
                  v-if="!isReturn(slip)"
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="onShowDetails(slip)"
                >Details</button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="pdfBusyId !== null"
                  @click="onDownloadPdf(slip)"
                >{{ pdfBusyId === slip.id ? 'Lädt …' : 'PDF' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="pdfError" class="error">{{ pdfError }}</div>
      </div>
    </main>

    <DeliverySlipDetailsModal ref="detailsModal" />
    <DeliverySlipRecipientModal ref="recipientModal" />
    <DeliverySlipCreateModal ref="createModal" />
    <AlertModal ref="alertModal" :title="alertTitle" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import TopNav from '../components/TopNav.vue';
import {
  downloadPdf,
  list,
  type DeliverySlip,
  type DeliverySlipResult,
  type SlipDirection,
} from '../services/deliverySlips';
import DeliverySlipDetailsModal from '../components/DeliverySlipDetailsModal.vue';
import DeliverySlipRecipientModal from '../components/DeliverySlipRecipientModal.vue';
import DeliverySlipCreateModal from '../components/DeliverySlipCreateModal.vue';
import AlertModal from '../components/AlertModal.vue';

/**
 * Übersicht ALLER Lieferscheine — hinaus zu den Servicepartnern und zurück von
 * ihnen. Beide liegen in derselben Tabelle und derselben Nummernreihe: wer eine
 * RL-Nummer in der Hand hat, soll sie hier nachschlagen können, ohne vorher zu
 * wissen, in welche Richtung das Paket ging.
 *
 * Von hier aus lässt sich auch ein neuer erstellen — derselbe Weg wie aus dem
 * Gerätelager, damit es nur eine Umsetzung gibt.
 */
const items     = ref<DeliverySlip[]>([]);
const loading   = ref(false);
const loadError = ref<string | null>(null);
const filter    = ref('');
/** '' = beide Richtungen. */
const direction = ref<'' | SlipDirection>('');
const pdfBusyId = ref<number | null>(null);
const pdfError  = ref<string | null>(null);

const detailsModal   = useTemplateRef<InstanceType<typeof DeliverySlipDetailsModal>>('detailsModal');
const recipientModal = useTemplateRef<InstanceType<typeof DeliverySlipRecipientModal>>('recipientModal');
const createModal    = useTemplateRef<InstanceType<typeof DeliverySlipCreateModal>>('createModal');
// Titel je Ausgang: „erstellt" oder „erstellt, aber nicht gedruckt".
const alertTitle     = ref('Lieferschein erstellt');
const alertModal     = useTemplateRef<InstanceType<typeof AlertModal>>('alertModal');

const visibleItems = computed<DeliverySlip[]>(() => {
  const needle = filter.value.trim().toLowerCase();
  const dir    = direction.value;
  return items.value.filter(slip => {
    if (dir !== '' && slip.direction !== dir) return false;
    if (needle === '') return true;
    return slip.slip_number.toLowerCase().includes(needle)
      || slip.sender.toLowerCase().includes(needle)
      || slip.recipient.toLowerCase().includes(needle)
      || slip.created_by.toLowerCase().includes(needle);
  });
});

function isReturn(slip: DeliverySlip): boolean {
  return slip.direction === 'from_partner';
}

onMounted(async () => {
  await reload();
});

async function reload(): Promise<void> {
  loading.value   = true;
  loadError.value = null;
  try {
    items.value = await list();
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Die Lieferscheine konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

function onShowDetails(slip: DeliverySlip): void {
  detailsModal.value?.open(slip);
}

async function onDownloadPdf(slip: DeliverySlip): Promise<void> {
  if (pdfBusyId.value !== null) return;
  pdfBusyId.value = slip.id;
  pdfError.value  = null;
  try {
    await downloadPdf(slip);
  } catch (e) {
    pdfError.value = e instanceof Error ? e.message : 'Das PDF konnte nicht geladen werden.';
  } finally {
    pdfBusyId.value = null;
  }
}

/**
 * Erst der Empfänger, dann die Fälle samt Druckerauswahl. Beide Modals lösen erst
 * im hidden-Event auf, damit sich die Backdrops nicht überlagern.
 */
async function onCreateDeliverySlip(): Promise<void> {
  const recipient = await recipientModal.value?.open();
  if (!recipient) return;
  const created = await createModal.value?.open(recipient);
  if (!created) return;
  await reload();
  alertTitle.value = created.printed
    ? 'Lieferschein erstellt'
    : 'Lieferschein erstellt, aber nicht gedruckt';
  await alertModal.value?.open(slipResultMessage(created));
}

/**
 * Rückmeldung zum Lieferschein. „Nicht gedruckt" ist kein Fehler — der
 * Lieferschein steht, das Papier liegt in der Druckwarteschlange.
 */
function slipResultMessage(created: DeliverySlipResult): string {
  const head = `Lieferschein ${created.slip_number} mit ${created.device_count} Gerät(en) erstellt`;
  return created.printed ? `${head} und gedruckt.` : `${head}. ${created.print_warning ?? ''}`.trim();
}

/**
 * Nichts übernommen = neutral, vollständig = ruhig grün, teilweise = auffällig:
 * genau der Zustand, den jemand ansehen muss.
 */
function takenClass(slip: DeliverySlip): string {
  if (slip.device_count === 0) return 'taken-none';
  if (slip.arrived_count === 0) return 'taken-none';
  if (slip.arrived_count >= slip.device_count) return 'taken-full';
  return 'taken-partial';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}
</script>

<style scoped>
.taken-none { color: var(--muted, #6c757d); }

.taken-full {
  color: #146c43;
  font-weight: 500;
}

/* Teilweise übernommen heißt: ein Gerät fehlt. Das soll auffallen. */
.taken-partial {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(247, 166, 0, 0.16);
  color: #8a5c00;
  font-weight: 600;
}

/* Neun Spalten - Absender und Empfaenger sind dazugekommen. Enger wuerde die
   Tabelle waagrecht scrollen, und das trifft immer die Aktionen rechts. */
.dashboard {
  max-width: 1400px;
}

.slips-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  /* Wie der Kopf der anderen Seiten - der Titel steht jetzt unter der
     Kopfzeile des Werkzeugs und braucht denselben Abstand nach unten. */
  margin-bottom: 24px;
}

.slips-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.slips-filter {
  width: 260px;
}

.slips-direction {
  width: 170px;
}

/* Hinaus ist der Alltag und bleibt ruhig; zurück heisst: da kommt etwas an. */
.dir-out {
  color: var(--muted, #6c757d);
}

.dir-back {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(13, 110, 253, 0.12);
  color: #0a58ca;
  font-weight: 600;
}

.slips-empty {
  color: #6c757d;
}

.slips-number {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.slips-count {
  text-align: right;
}

.slips-row-actions {
  display: flex;
  gap: 0.4rem;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #ced4da;
  border-top-color: #0d6efd;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}
</style>
